/**
 * Categorías de producto Consumo / Farma (Medifarma Perú, reporte 7).
 *
 * Fuente: `reportes_fidelizacion/consumo-farma.json`. Las listas se embeben aquí
 * (mismo patrón que `preciosReferencia.ts`) y se usan para clasificar los
 * productos que devuelve el upstream, cuyos nombres NO coinciden de forma exacta
 * con estas referencias (variaciones de OCR, espacios, tildes, presentación).
 */
import { claveCadena } from './cadenas'

export type CategoriaProducto = 'Consumo' | 'Farma'

export const CATEGORIAS_PRODUCTO: CategoriaProducto[] = ['Consumo', 'Farma']

const CATEGORIAS: Record<CategoriaProducto, string[]> = {
  Farma: [
    'Uropol Forte F 100',
    'Solutres 150mg',
    'Pyridium Forte 200mg',
    'Paldolor Extra Forte F',
    'Notil',
    'Magal D',
    'Doloral Suspension 100mg',
    'Doloral Forte Suspension 200mg',
    'Ak Trol',
    'Ab Broncol',
  ],
  Consumo: [
    'PVM Junior',
    'PVM Adulto',
    'Nutriben Crecimiento',
    'Nutriben Confort',
    'Nistazinc',
    'Menthobioticos',
    'Leche de Magnesia',
    'Ibumejoral Mujer',
    'Electrolight',
    'Zaidman Emulsion',
  ],
}

/**
 * Normaliza un nombre de producto para comparar: `claveCadena` (minúsculas, sin
 * tildes, espacios colapsados) y además une el espacio entre un número y su
 * unidad ("800 g" → "800g", "200 mg" → "200mg"), variación común del OCR. No
 * elimina el resto de espacios para no crear falsos positivos (p. ej. "magal d"
 * no debe caer dentro de "magaldrato").
 */
function normalizarProducto(nombre: string): string {
  return claveCadena(nombre).replace(/-/g, ' ').replace(/\s+/g, ' ').replace(/(\d)\s+([a-z])/g, '$1$2').trim()
}

// Referencias normalizadas una sola vez.
const REFS: Record<CategoriaProducto, string[]> = {
  Farma: CATEGORIAS.Farma.map(normalizarProducto).filter(Boolean),
  Consumo: CATEGORIAS.Consumo.map(normalizarProducto).filter(Boolean),
}

/**
 * ¿El nombre de un producto pertenece a la categoría? Coincidencia NO exacta:
 * normaliza ambos lados y considera match si el nombre del producto contiene
 * alguna referencia de la categoría (o la referencia contiene al nombre).
 */
export function productoEnCategoria(
  nombre: string | null | undefined,
  categoria: CategoriaProducto,
): boolean {
  if (!nombre) return false
  const n = normalizarProducto(nombre)
  if (!n) return false
  return REFS[categoria].some((r) => n.includes(r) || r.includes(n))
}

/**
 * Conjunto de `producto_id` (como string) del desglose por producto que
 * pertenecen a la categoría. Permite combinar la categoría con el filtro de
 * producto existente, que opera por id.
 */
export function idsProductoDeCategoria(
  datos: Array<{ producto_id: number; nombre: string }>,
  categoria: CategoriaProducto,
): Set<string> {
  const out = new Set<string>()
  for (const d of datos) {
    if (productoEnCategoria(d.nombre, categoria)) out.add(String(d.producto_id))
  }
  return out
}
